import { useSuspenseQuery } from "@tanstack/react-query";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import { addError } from "@wayflyer/blackbox-fe";

import { useFundingApplication } from "../../queries/funding-application/queries";
import { getFundingApplicationStagesQueryOptions } from "../../queries/funding-application-configuration/queries";

import { LinearNavigationContext, calculateJourneyFromStages } from "./use-funding-application-navigation-next";

interface LinearNavigationContextProviderProps {
  children: any;
  countryOfIncorporation?: string;
  customerDomain: string;
}

const hasNoConfiguredStages = (
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
) => {
  return (
    fundingApplicationStages.revenue === null &&
    fundingApplicationStages.marketing === null &&
    fundingApplicationStages.accounting === null &&
    fundingApplicationStages.banking === null &&
    fundingApplicationStages.creditConsent === null &&
    fundingApplicationStages.documents?.length === 0
  );
};

interface StageBasedLinearNavigationContextProviderProps {
  children: any;
  customerDomain: string;
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
}

/**
 * Navigation provider that fetches only the data needed for stage-based journey calculation.
 */
const StageBasedLinearNavigationContextProvider = ({
  children,
  customerDomain,
  fundingApplicationStages,
}: StageBasedLinearNavigationContextProviderProps) => {
  const { data: fundingApplication } = useFundingApplication(customerDomain);

  if (fundingApplication.applicationState === "in_progress" && hasNoConfiguredStages(fundingApplicationStages)) {
    addError(new Error("No stages configured for in progress application"));
  }

  const navigationFlow = calculateJourneyFromStages(fundingApplication, fundingApplicationStages);

  return <LinearNavigationContext.Provider value={navigationFlow}>{children}</LinearNavigationContext.Provider>;
};

export const LinearNavigationContextProvider = ({ children, customerDomain }: LinearNavigationContextProviderProps) => {
  const { data: fundingApplicationStages } = useSuspenseQuery(getFundingApplicationStagesQueryOptions(customerDomain));

  return (
    <StageBasedLinearNavigationContextProvider
      customerDomain={customerDomain}
      fundingApplicationStages={fundingApplicationStages}
    >
      {children}
    </StageBasedLinearNavigationContextProvider>
  );
};
